import * as endpoints from "../../lib/api/endpoints";
import * as error from "../../lib/api/error";
import * as queryStringHelpers from "../../lib/queryStringHelpers";
import * as routes from "../../constants/routes";
import * as validationLib from "../../lib/validation";
import ActionButton, {ActionButtonStyle} from "../ActionButton";
import BoxInput from "../BoxInput";
import callApplicationLead from "../../lib/callApplicationLead";
import cardsImage from "../../images/stacked-cards.png";
import cardsImage2 from "../../images/stacked-cards@2x.png";
import cardsImage3 from "../../images/stacked-cards@3x.png";
import ErrorBoundary from "../ErrorBoundary";
import ExpiredInviteModal from "../ExpiredInviteModal";
import getUserIds from "../../lib/getUserIds";
import isAddressKey from "../../lib/isAddressKey";
import isApplicationKey from "../../lib/isApplicationKey";
import LandingPageWrapper from "../LandingPageWrapper";
import React, {useState, useEffect, useRef} from "react";
import SetNoIndex from "../../lib/SetNoIndex";
import styles from "./InviteLandingPage.module.css";
import usePageView from "../../lib/usePageView";
import ValidationList from "../ValidationList";
import {Container, Row, Column} from "../grid";
import {InviteValidationStatus, AppKeys, AddressKeys} from "../../lib/api/models";
import {RouteComponentProps} from "react-router-dom";
import {checkForDefunctCode} from "../OrganicApplicationPage/OrganicApplicationPage";

const strings = {
    title: "1st Financial Bank USA Invitation",
    card: "Credit card",
    continue: "Continue",
    description:
        "This card offer is reserved for students age 18 and older who have been issued an exclusive Invitation Code Number.",
    expiredModal: "Expired Invite Code Modal",
    inviteCode: "Invitation Code Number",
    zipCode: "5-Digit ZIP Code",
    next: "Next",
    forgot: "Forgot My Invitation Number",
};

const IS_DEV = process.env.NODE_ENV === "development";

const DISABLE_ANALYTICS_CODE = "300";

function getForgotInviteUrl(): string {
    return `${routes.FORGOT_INVITE}${window.location.search}`;
}

export default function InviteLandingPage(props: RouteComponentProps) {
    useEffect(() => {
        document.title = strings.title;
    }, []);
    SetNoIndex();
    usePageView(strings.title);

    useEffect(() => {
        const qs = queryStringHelpers.objectify(window.location.search);
        if (qs.m !== "300") {
            endpoints
                .visit(
                    getUserIds().visitID,
                    document.documentURI,
                    navigator.userAgent,
                    document.referrer,
                )
                .catch((err) => error.reportAxios(err, "InviteLandingPage"));
        }
    }, []);

    const queryParams = queryStringHelpers.objectify(window.location.search);
    const windowUrl = window.location.href;

    const defaultInviteCode = IS_DEV ? "123456789" : "";
    const defaultZipCode = IS_DEV ? "12345" : "";
    const [inviteCode, setInviteCode] = useState(
        queryParams.dsn ? queryParams.dsn : defaultInviteCode,
    );
    const [zipCode, setZipCode] = useState(queryParams.zip ? queryParams.zip : defaultZipCode);
    const [validation, setValidation] = useState<validationLib.Validation>({});
    const [loading, setLoading] = useState<boolean>(false);

    const [expiredUrlCode, setExpiredUrlCode] = useState<string>("");
    const [canRedirectExpiredApp, setCanRedirectExpiredApp] = useState<boolean>(false);

    const inviteHasChanged = useRef(false);
    const zipCodeHasChanged = useRef(false);

    const isMountedRef = useRef<boolean>(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const continueBtnRef = useRef(null);
    const [expiredModal, setExpiredModal] = useState(false);

    /* Only redirect after expired modal has been closed */
    useEffect(() => {
        if (!isMountedRef.current) {
            return;
        }
        const qp = queryStringHelpers.objectify(window.location.search);
        if (!expiredModal && canRedirectExpiredApp) {
            const paramsToStringify: {[key: string]: string} = {
                ...qp,
                source: queryStringHelpers.Values.PalExpired,
            };
            if (expiredUrlCode && checkForDefunctCode(expiredUrlCode) !== null) {
                paramsToStringify.cid = expiredUrlCode;
            }
            const stringParams = queryStringHelpers.stringify(paramsToStringify);
            props.history.push(`${routes.ORGANIC_APP}${stringParams}`);
            setCanRedirectExpiredApp(false);
        }
    }, [isMountedRef, expiredModal, canRedirectExpiredApp, expiredUrlCode, props.history]);

    function closeExpiredModal() {
        setExpiredModal(false);
    }
    function closeExpiredModalAndRedirect() {
        if (!isMountedRef.current) {
            return;
        }
        closeExpiredModal();
        setCanRedirectExpiredApp(true);
    }
    function openExpiredModal() {
        setExpiredModal(true);
    }

    function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        setLoading(true);
        endpoints
            .invitationValidate(inviteCode, zipCode)
            .then((data) => {
                if (!isMountedRef.current) {
                    return;
                }
                if (queryParams.m !== DISABLE_ANALYTICS_CODE) {
                    callApplicationLead("invite-validate", null, windowUrl);
                }
                if (data.status === InviteValidationStatus.active) {
                    props.history.push(`${routes.INVITE_APP}${window.location.search}`, {
                        ...data.applicationInfo,
                        invitationCode: inviteCode,
                    });
                } else if (data.status === InviteValidationStatus.expired) {
                    // Show Modal and redirect to Oragnic w/ urlCode
                    if (data.urlCode) {
                        setExpiredUrlCode(data.urlCode);
                    }
                    openExpiredModal();
                } else if (data.status === InviteValidationStatus.tooManyAttempts) {
                    // Redirect to Organic w/ urlCode
                    const paramsToStringify: {[key: string]: string} = {
                        ...queryParams,
                        source: queryStringHelpers.Values.PalUnknown,
                    };
                    if (data.urlCode) {
                        paramsToStringify.cid = data.urlCode;
                    }
                    const stringParams = queryStringHelpers.stringify(paramsToStringify);
                    props.history.push(`${routes.ORGANIC_APP}${stringParams}`);
                }
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                const errValidation = validationLib.getValidationObj(err);
                setValidation(errValidation);
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setLoading(false);
            });
    }

    useEffect(() => {
        const qp = queryStringHelpers.objectify(window.location.search);
        // Validate query params if provided
        if (qp.dsn && qp.zip) {
            setLoading(true);
            endpoints
                .invitationValidate(qp.dsn, qp.zip)
                .then((data) => {
                    if (!isMountedRef.current) {
                        return;
                    }
                    if (qp.m !== DISABLE_ANALYTICS_CODE) {
                        callApplicationLead("scan", qp.dsn, window.location.href);
                    }

                    if (data.status === InviteValidationStatus.active) {
                        props.history.push(`${routes.INVITE_APP}${window.location.search}`, {
                            ...data.applicationInfo,
                            invitationCode: qp.dsn,
                        });
                    } else if (data.status === InviteValidationStatus.expired) {
                        // Show Modal and redirect to Oragnic w/ urlCode
                        if (data.urlCode) {
                            setExpiredUrlCode(data.urlCode);
                        }
                        openExpiredModal();
                    } else if (data.status === InviteValidationStatus.tooManyAttempts) {
                        // Redirect to Organic w/ urlCode
                        const paramsToStringify = {...qp};
                        if (data.urlCode) {
                            paramsToStringify.cid = data.urlCode;
                        }
                        const stringParams = queryStringHelpers.stringify(paramsToStringify);
                        props.history.push(`${routes.ORGANIC_APP}${stringParams}`);
                    }
                })
                .catch((err) => {
                    if (!isMountedRef.current) {
                        return;
                    }
                    const errValidation = validationLib.getValidationObj(err);
                    setValidation(errValidation);
                })
                .finally(() => {
                    if (!isMountedRef.current) {
                        return;
                    }
                    setLoading(false);
                });
        }
    }, [props.history]);

    function onBlurInput(e: React.FocusEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;
        if (isApplicationKey(name) || isAddressKey(name)) {
            if (name === AppKeys.InviteCode && inviteHasChanged.current) {
                inviteHasChanged.current = false;
                if (queryParams.m !== DISABLE_ANALYTICS_CODE) {
                    callApplicationLead(AppKeys.InviteCode, null, windowUrl, value);
                }
            } else if (name === AddressKeys.Zipcode && zipCodeHasChanged.current) {
                zipCodeHasChanged.current = false;
                if (queryParams.m !== DISABLE_ANALYTICS_CODE) {
                    callApplicationLead(AddressKeys.Zipcode, null, windowUrl, value);
                }
            }
        }
    }

    function onChangeInvite(e: React.ChangeEvent<HTMLInputElement>) {
        setInviteCode(e.target.value);
        inviteHasChanged.current = true;
        if (validation.generic || validation?.errors?.inviteCode) {
            setValidation((prev) => {
                const next = {...prev};
                delete next.generic;
                delete next.errors?.inviteCode;
                return next;
            });
        }
    }

    function onChangeZip(e: React.ChangeEvent<HTMLInputElement>) {
        setZipCode(e.target.value);
        zipCodeHasChanged.current = true;
        if (validation.generic || validation?.errors?.zipCode) {
            setValidation((prev) => {
                const next = {...prev};
                delete next.generic;
                delete next.errors?.zipCode;
                return next;
            });
        }
    }

    return (
        <ErrorBoundary>
            <LandingPageWrapper renderInterestRates={false}>
                <form onSubmit={onSubmit}>
                    <Container>
                        <Row className={styles.row}>
                            <Column large={8} className={styles.cardsImageColumn}>
                                <div className={styles.cardsWrapper}>
                                    <img
                                        className={styles.cardsImage}
                                        src={cardsImage}
                                        srcSet={`${cardsImage} 1x, ${cardsImage2} 2x, ${cardsImage3} 3x`}
                                        alt={strings.card}
                                    />
                                </div>
                            </Column>
                            <Column large={4} className={styles.infoColumn}>
                                <div className={styles.description}>{strings.description}</div>
                                <BoxInput
                                    className={styles.inviteCode}
                                    name={AppKeys.InviteCode}
                                    value={inviteCode}
                                    onBlur={onBlurInput}
                                    onChange={onChangeInvite}
                                    label={strings.inviteCode}
                                    placeholder={strings.inviteCode}
                                    minLength={9}
                                    maxLength={9}
                                    pattern="\d{9}"
                                    required={true}
                                    validationMessage={validation?.errors?.inviteCode}
                                    inputMode="numeric"
                                />
                                <BoxInput
                                    className={styles.zipCode}
                                    name={AddressKeys.Zipcode}
                                    value={zipCode}
                                    onBlur={onBlurInput}
                                    onChange={onChangeZip}
                                    label={strings.zipCode}
                                    placeholder={strings.zipCode}
                                    minLength={5}
                                    maxLength={5}
                                    pattern="\d{5}"
                                    required={true}
                                    validationMessage={validation?.errors?.zipCode}
                                    inputMode="numeric"
                                />
                                {validation.generic ? (
                                    <ValidationList
                                        className={styles.validation}
                                        messages={validation.generic}
                                    />
                                ) : null}
                                <ActionButton
                                    className={styles.next}
                                    style={ActionButtonStyle.secondary}
                                    label={strings.next}
                                    loading={loading}
                                    fullWidth={true}
                                    type="submit"
                                />
                                <ActionButton
                                    style={ActionButtonStyle.outlinedDark}
                                    label={strings.forgot}
                                    onClick={() => {
                                        if (queryParams.m !== DISABLE_ANALYTICS_CODE) {
                                            callApplicationLead("invite-forgot", null, windowUrl);
                                        }
                                    }}
                                    linkTo={getForgotInviteUrl()}
                                    openInSameTab={true}
                                    fullWidth={true}
                                    type="button"
                                />
                            </Column>
                            <ExpiredInviteModal
                                buttonRef={continueBtnRef}
                                label={strings.expiredModal}
                                isOpen={expiredModal}
                                onClose={closeExpiredModal}
                                onSubmit={closeExpiredModalAndRedirect}
                                onSubmitLabel={strings.continue}
                            />
                        </Row>
                    </Container>
                </form>
            </LandingPageWrapper>
        </ErrorBoundary>
    );
}
